import { supabase } from '@/lib/supabase';
import type { Profile, TablesUpdate } from '@/types';

/** Fetch a profile by its auth user id. */
export async function getProfileById(id: string): Promise<Profile | null> {
  const { data, error } = await supabase.from('profiles').select('*').eq('id', id).maybeSingle();
  if (error) throw error;
  return data;
}

/**
 * Fetch a profile by its public `@handle`. Handles are stored WITH the leading
 * `@` (onboarding writes `'@' + handle`, _v9_onboarding.js:467), so we normalize
 * the lookup to always include it whether the route param has one or not.
 */
export async function getProfileByHandle(handle: string): Promise<Profile | null> {
  const normalized = handle.startsWith('@') ? handle : `@${handle}`;
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('handle', normalized)
    .maybeSingle();
  if (error) throw error;
  return data;
}

/** The cosmetic fields a user can change from their own profile. */
export interface CosmeticPatch {
  title_id?: string;
  emblem_id?: string;
}

/**
 * The editable public-profile fields, ported from the OG settings Profile tab
 * onto the profile page. `handle` is stored WITHOUT the leading `@` in the draft.
 */
export interface ProfileFieldsPatch {
  displayName: string;
  handle: string;
  avatarUrl: string;
  bio: string;
  status: string;
}

/**
 * Persist the editable public-profile fields to the user's `profiles` row. Same
 * column mapping + "omit empties" as the OG `saveDraft` (so we never null out an
 * unchanged column). Deliberately does NOT touch notification/privacy prefs —
 * those stay owned by the Settings page.
 */
export async function updateProfileFields(
  id: string,
  fields: ProfileFieldsPatch,
  /** Stamp `display_name_changed_at = now()` — set only on a real name change. */
  stampNameChange = false,
): Promise<Profile> {
  const patch: TablesUpdate<'profiles'> = {
    display_name: fields.displayName || undefined,
    handle: fields.handle ? `@${fields.handle}` : undefined,
    avatar_url: fields.avatarUrl || undefined,
    bio: fields.bio || undefined,
    status: fields.status || undefined,
    display_name_changed_at: stampNameChange ? new Date().toISOString() : undefined,
  };
  // Drop undefined keys so we never null out an unchanged column (OG parity).
  (Object.keys(patch) as (keyof typeof patch)[]).forEach(
    (k) => patch[k] === undefined && delete patch[k],
  );

  const { data, error } = await supabase
    .from('profiles')
    .update(patch)
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw error;
  return data;
}

/** Persist the social links map (`profiles.links`). */
export async function updateProfileLinks(
  id: string,
  links: Record<string, string>,
): Promise<Profile> {
  const { data, error } = await supabase
    .from('profiles')
    .update({ links })
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw error;
  return data;
}

/** The About-section visibility flags a user controls on their own profile. */
export interface VisibilityPatch {
  show_friends?: boolean;
  show_communities?: boolean;
}

/**
 * Persist an About-section visibility toggle to the user's `profiles` row. The
 * flags are world-readable (so the public profile can honor them) but only the
 * owner can change them — enforced by the `profiles_self_update` RLS policy.
 */
export async function updateProfileVisibility(
  id: string,
  patch: VisibilityPatch,
): Promise<Profile> {
  const { data, error } = await supabase
    .from('profiles')
    .update(patch)
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw error;
  return data;
}

/**
 * Persist a cosmetic change (title / emblem) to the user's `profiles` row.
 * Mirrors the OG `saveProfileCosmetic` (supabase.js:144) — same columns, same
 * shared table. Skin selection is handled separately by the theme feature.
 */
export async function updateProfileCosmetic(id: string, patch: CosmeticPatch): Promise<Profile> {
  const dbPatch: TablesUpdate<'profiles'> = patch;
  const { data, error } = await supabase
    .from('profiles')
    .update(dbPatch)
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw error;
  return data;
}
